import { FieldDefinition } from '../field-definition';
import { GenericValidator } from '../../generic-form/model/generic-validator';
import { GenericFormField } from '../../generic-form/model/generic-field';
import { ValidationTypes } from '../../generic-form/enums/validation-types';
import { ControlTypes } from '../../generic-form/enums/control-types';
import { InputTypes } from '../../generic-form/enums/input-type';
import { ValidationConverterService } from '../../services/validation-converter.service';
import { CustomDateRangeFilter } from '../../generic-table/filters/custom-date-range-filter';
import { BaseFieldBuilder } from './field-builder.base';
import { CustomDateEditor } from '../../generic-table/editors/custom-date-editor';
import { dateOnlyFormatter } from '../../generic-table/formatters/date-only-formatter';

export class DateFieldBuilder extends BaseFieldBuilder {
  private readonly validators: GenericValidator[] = [];
  private _required = false;

  constructor(key: string, label: string) {
    super(key, label, ControlTypes.Date);
    this._type = InputTypes.Date;
  }

  required(): this {
    this._required = true;
    this.validators.push(new GenericValidator({ key: ValidationTypes.Required }));
    return this;
  }

  equalOrLessThanField(fieldKey: string, fieldLocalization?: string): this {
    this.validators.push(
      new GenericValidator({
        key: ValidationTypes.EqualOrLessThanField,
        value: fieldKey,
        fieldLocalization,
      }),
    );
    return this;
  }

  lessThanField(fieldKey: string, fieldLocalization?: string): this {
    this.validators.push(
      new GenericValidator({
        key: ValidationTypes.LessThanField,
        value: fieldKey,
        fieldLocalization,
      }),
    );
    return this;
  }

  equalOrGreaterThanField(fieldKey: string, fieldLocalization?: string): this {
    this.validators.push(
      new GenericValidator({
        key: ValidationTypes.EqualOrGreaterThanField,
        value: fieldKey,
        fieldLocalization,
      }),
    );
    return this;
  }

  greaterThanField(fieldKey: string, fieldLocalization?: string): this {
    this.validators.push(
      new GenericValidator({
        key: ValidationTypes.GreaterThanField,
        value: fieldKey,
        fieldLocalization,
      }),
    );
    return this;
  }

  build(): FieldDefinition {
    const column = this.getBaseColumnConfig();
    const localizedLabel = this.getLocalizationKey();

    this.ensureDateValidator();
    this.configureFormatter(column);
    this.configureEditor(column);
    this.configureFilter(column);

    return {
      key: this.key,
      label: localizedLabel,
      column,
      fieldOptions: this.createFieldOptions(localizedLabel),
    };
  }

  private ensureDateValidator(): void {
    const hasDateValidator = this.validators.some(v => v.key === ValidationTypes.Date);
    if (hasDateValidator) {
      return;
    }

    this.validators.push(new GenericValidator({ key: ValidationTypes.Date }));
  }

  private configureFormatter(column: any): void {
    if (column.formatter) {
      return;
    }

    column.formatter = dateOnlyFormatter;
    column.params = {
      ...column.params,
      parseDateAsUtc: true,
    };
  }

  private configureEditor(column: any): void {
    if (this.readOnlyValue) {
      return;
    }

    column.createEditor = (localizationService: any) => {
      const validationConverter = new ValidationConverterService(localizationService);
      return {
        model: CustomDateEditor,
        required: this._required,
        editorOptions: {
          allowInput: true,
          altFormat: 'd/m/Y',
          altInput: true,
          dateFormat: 'Y/m/d',
          enableTime: false,
        },
        validator: validationConverter.toSlickGridValidator(
          this.validators,
          this._required,
          this._controlType,
        ),
      };
    };
  }

  private configureFilter(column: any): void {
    column.createFilter = (localizationService: any) => {
      const params: Record<string, any> = {
        localizationService,
        includeTime: false,
      };

      if (this.filterInputEmptyValueValue) {
        params.inputEmptyValue = this.filterInputEmptyValueValue;
      }

      return {
        model: CustomDateRangeFilter,
        params,
      };
    };
  }

  private createFieldOptions(localizedLabel: string): GenericFormField {
    return new GenericFormField({
      key: this.key,
      label: localizedLabel,
      controlType: this._controlType,
      type: this._type,
      required: this._required,
      validators: this.validators,
      hidden: this.hideInChildFormsValue,
      hiddenByDefault: this.hideByDefaultValue,
      readOnly: this.readOnlyValue,
      showReadOnlyInForm: this.showReadOnlyInFormValue,
      dependencies: this.dependencyRules.length ? this.dependencyRules : undefined,
      prefilter: this.prefilterOptions,
    });
  }
}
